import type {
	TApplePayConfig,
	TApplePayContactValidationResult,
	TApplePayResponseValidationEvent,
} from 'coreClasses/applepay/types/TApplePayAPI';
import type {
	ApplePaySession as TApplePaySession,
	ApplePayError as TApplePayError,
	ApplePayJS as TApplePayJS,
} from 'coreClasses/applepay/types/TApplePayWebAPI';
import type TPaymentResult from 'coreClasses/payments/types/TPaymentResult';
import type ManagerRepository from 'coreClasses/ManagerRepository';

const APPLE_PAY_API_SUPPORTED = {
	MIN: 3,
	MAX: 4,
};

/**
 * Represents ApplePayDefaultHandler
 */
class ApplePayDefaultHandler {
	protected handlePaymentAuthorized: (redirectURL: string) => void;
	protected handleError: (error: Error) => void;
	protected handleCancel: () => void;
	protected managerRepository: ManagerRepository;
	protected config: TApplePayConfig;
	protected session?: TApplePaySession;
	protected cachedTotal: TApplePayJS.ApplePayLineItem;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - manager repository
	 * @param {TApplePayConfig} config - ApplePay configuration
	 */
	constructor(managerRepository: ManagerRepository, config: TApplePayConfig) {
		this.managerRepository = managerRepository;
		this.config = config;
		this.session = null;
	}

	/**
	 * Handle Apple Pay payment processing
	 * @param {string} [productID] - product ID
	 * @returns {Promise<TPaymentResult>} - promise with payment result
	 */
	handlePayment(productID?: string): Promise<TPaymentResult> {
		if (productID) {
			this.prepareBasket(productID);
		}

		this.createSession();

		return new Promise((resolve) => {
			this.handlePaymentAuthorized = (redirectURL: string) => {
				resolve({
					success: true,
					status: 'completed',
					redirectURL: redirectURL,
				});
			};

			this.handleError = (error: Error) => {
				resolve({
					success: false,
					status: 'error',
					error: error,
				});
			};

			this.handleCancel = () => {
				resolve({
					success: false,
					status: 'canceled',
				});
			};

			this.session.begin();
		});
	}

	/**
	 * Prepares basket for Apple Pay payment processing
	 * @protected
	 * @param {string} [productSku] - product ID for preparing a basket with
	 */
	protected prepareBasket(productSku?: string) {
		this.managerRepository
			.getSfccAPI()
			.applePayPrepareBasket(productSku)
			.catch((error: Error) => this.onHandleError(error));
	}

	/**
	 * Creates Apple Pay session
	 * @protected
	 */
	protected createSession() {
		this.session = new window.ApplePaySession(this.getSupportedVersion(), this.getInitialRequest());

		this.session.onvalidatemerchant = this.onValidateMerchant.bind(this);
		this.session.onpaymentmethodselected = this.onPaymentMethodSelected.bind(this);
		this.session.onshippingcontactselected = this.onShippingContactSelected.bind(this);
		this.session.onshippingmethodselected = this.onShippingMethodSelected.bind(this);
		this.session.onpaymentauthorized = this.onPaymentAuthorized.bind(this);
		this.session.oncancel = this.onCancel.bind(this);
	}

	/**
	 * Returns Apple Pay session API supported version
	 * @protected
	 * @returns {number|null} - Apple Pay session API version
	 */
	protected getSupportedVersion(): number | null {
		for (let apVersion = APPLE_PAY_API_SUPPORTED.MAX; apVersion >= APPLE_PAY_API_SUPPORTED.MIN; apVersion--) {
			if (window.ApplePaySession.supportsVersion(apVersion)) {
				return apVersion;
			}
		}

		return null;
	}

	/**
	 * Returns Apple Pay payment request
	 * @protected
	 * @returns {TApplePayJS.ApplePayPaymentRequest} - a request for payment, which includes information about payment-processing capabilities
	 */
	protected getInitialRequest(): TApplePayJS.ApplePayPaymentRequest {
		return {
			countryCode: this.config.countryCode,
			currencyCode: this.config.currencyCode,
			merchantCapabilities: this.config.merchantCapabilities,
			supportedNetworks: this.config.supportedNetworks,
			requiredShippingContactFields: this.config.requiredShippingContactFields,
			requiredBillingContactFields: this.config.requiredBillingContactFields,
			total: {
				label: this.config.merchantName,
				type: 'final',
				amount: '0',
			},
		};
	}

	/**
	 * Handles Apple Pay session merchant validation callback @see {TApplePaySession.onvalidatemerchant}
	 * @protected
	 * @param {TApplePayJS.ApplePayValidateMerchantEvent} eventObj - Apple Pay session validation event object that contains the validation URL
	 */
	protected onValidateMerchant(eventObj: TApplePayJS.ApplePayValidateMerchantEvent) {
		this.managerRepository
			.getSfccAPI()
			.applePayOnValidateMerchant({
				isTrusted: eventObj.isTrusted,
				validationURL: eventObj.validationURL,
				hostname: window.location.host,
			})
			.then((response) => {
				if (this.session) {
					this.session.completeMerchantValidation(response.session);
				}
			})
			.catch((error: Error) => this.onHandleError(error));
	}

	/**
	 * Handles the Apple Pay session payment method selection callback @see {TApplePaySession.onpaymentmethodselected}
	 * @protected
	 * @param {TApplePayJS.ApplePayPaymentMethodSelectedEvent} eventObj - an event object that contains the payment method
	 */
	protected onPaymentMethodSelected(eventObj: TApplePayJS.ApplePayPaymentMethodSelectedEvent) {
		this.managerRepository
			.getSfccAPI()
			.applePayOnPaymentMethodSelected(eventObj)
			.then((response) => {
				if (this.session) {
					const completeObject = {
						newTotal: response.total,
						newLineItems: response.lineItems,
					};

					this.cachedTotal = response.total;
					this.session.completePaymentMethodSelection(completeObject);
				}
			})
			.catch((error: Error) => this.onHandleError(error));
	}

	/**
	 * Handles the Apple Pay shipping contact selection callback @see {TApplePaySession.onshippingcontactselected}
	 * @protected
	 * @param {TApplePayJS.ApplePayShippingContactSelectedEvent} eventObj - an event object that contains the shipping address the user selects
	 */
	protected onShippingContactSelected(eventObj: TApplePayJS.ApplePayShippingContactSelectedEvent) {
		this.managerRepository
			.getSfccAPI()
			.applePayOnShippingContactSelected(eventObj)
			.then((response) => {
				if (this.session) {
					const completeObject = {
						newTotal: response.total,
						newLineItems: response.lineItems,
						newShippingMethods: response.shippingMethods,
						errors: this.getApplePayErrorsFromResponseEvent(response.event),
					};

					this.cachedTotal = response.total;
					this.session.completeShippingContactSelection(completeObject);
				}
			})
			.catch((error: Error) => this.onHandleError(error));
	}

	/**
	 * Handles the Apple Pay shipping method selection callback @see {TApplePaySession.onshippingmethodselected}
	 * @protected
	 * @param {TApplePayJS.ApplePayShippingMethodSelectedEvent} eventObj - an event object that contains the shipping address the user selects
	 */
	protected onShippingMethodSelected(eventObj: TApplePayJS.ApplePayShippingMethodSelectedEvent) {
		this.managerRepository
			.getSfccAPI()
			.applePayOnShippingMethodSelected(eventObj)
			.then((response) => {
				if (this.session) {
					/**
					 * Perhaps there is a bug with Apple Pay, as we CAN NOT remove all shipping methods by
					 * submitting @see {TApplePayJS.ApplePayShippingContactUpdate} object with an empty array
					 * assigned to the "newShippingMethods" property through Apple Pay session methods:
					 * @see {TApplePaySession.completePaymentMethodSelection}
					 * @see {TApplePaySession.completeShippingContactSelection}
					 * @see {TApplePaySession.completeShippingMethodSelection}
					 * @see {TApplePaySession.completeCouponCodeChange}
					 *
					 * As a result, we may encounter a situation where there are NO available shipping methods for the selected
					 * shipping contact. Yet, the user can still see and select a shipping method that was previously rendered.
					 * If the user selects an unavailable shipping method, we receive an error from the BE:
					 * {"error": "Unknown shipping method: <SHIPPING_METHOD_ID>", "status": "Failure"}
					 *
					 * However, to conclude this callback, we MUST submit at least the "newTotal" property of the
					 * @see {TApplePayJS.ApplePayShippingContactUpdate} object to the "TApplePaySession.complete..." method.
					 *
					 * Hence, to improve the UI and user experience, we submit the previous total value
					 * @see {ApplePayDefaultHandler.cachedTotal} rather than a dummy value.
					 */
					let completeObject: TApplePayJS.ApplePayShippingContactUpdate = {
						newTotal: this.cachedTotal,
					};

					if (!('error' in response)) {
						completeObject = {
							newTotal: response.total,
							newLineItems: response.lineItems,
						};

						this.cachedTotal = response.total;
					}

					this.session.completeShippingMethodSelection(completeObject);
				}
			})
			.catch((error: Error) => this.onHandleError(error));
	}

	/**
	 * Handles the Apple Pay payment authorization callback @see {TApplePaySession.onpaymentauthorized}
	 * @protected
	 * @param {TApplePayJS.ApplePayPaymentAuthorizedEvent} eventObj - an event object that contains the shipping address the user selects
	 */
	protected onPaymentAuthorized(eventObj: TApplePayJS.ApplePayPaymentAuthorizedEvent) {
		this.managerRepository
			.getSfccAPI()
			.applePayValidation(eventObj.payment)
			.then(({validationResult}) => {
				let errors = [];

				if (!validationResult.shippingContact.valid) {
					errors = errors.concat(
						this.getContactValidationErrors(validationResult.shippingContact, 'shippingContactInvalid'),
					);
				}

				if (!validationResult.billingContact.valid) {
					errors = errors.concat(
						this.getContactValidationErrors(validationResult.billingContact, 'billingContactInvalid'),
					);
				}

				return errors;
			})
			.then((errors: Array<TApplePayError>) => {
				if (errors.length) {
					this.session.completePayment({
						status: window.ApplePaySession.STATUS_FAILURE,
						errors: errors,
					});
				} else {
					this.managerRepository
						.getSfccAPI()
						.applePayOnPaymentAuthorized(eventObj)
						.then((response) => {
							if ('error' in response) {
								this.session.completePayment({status: window.ApplePaySession.STATUS_FAILURE});
								this.handleError(new Error(response.error));
							} else {
								this.session.completePayment({status: window.ApplePaySession.STATUS_SUCCESS});
								this.handlePaymentAuthorized(response.redirect);
							}
						})
						.catch((error: Error) => this.onHandleError(error));
				}
			})
			.catch((error: Error) => this.onHandleError(error));
	}

	/**
	 * Handles the Apple Pay payment cancel callback @see {TApplePaySession.oncancel}
	 * @protected
	 * @param {TApplePayJS.Event} eventObj - an event object
	 */
	protected onCancel() {
		// eslint-disable-line
		this.session = null;

		this.managerRepository.getSfccAPI().applePayCancel().then(this.handleCancel).catch(this.handleError);
	}

	/**
	 * Handles errors during processing Apple Pay payment
	 * @protected
	 * @param {Error} error - error
	 */
	private onHandleError(error: Error) {
		if (this.session) {
			this.session.abort();
		}

		this.handleError(error);
	}

	/**
	 * Returns Apple Pay errors from contact validation result
	 * @private
	 * @param {TApplePayContactValidationResult} contactValidationResult - object with contact validation result
	 * @param {TApplePayJS.ApplePayErrorCode} errorSection - error code that indicates whether an error on the payment sheet is for shipping or billing information, or for another kind of error
	 * @returns {Array<TApplePayError>} - an array of Apple Pay errors
	 */
	private getContactValidationErrors(
		contactValidationResult: TApplePayContactValidationResult,
		errorSection: TApplePayJS.ApplePayErrorCode,
	): Array<TApplePayError> {
		let errors = [];

		if (!contactValidationResult.valid) {
			const contactFields = Object.keys(contactValidationResult.fields);

			errors = contactFields.map((fieldName: TApplePayJS.ApplePayErrorContactField) => {
				const {valid, ...fieldRules} = contactValidationResult.fields[fieldName];
				const errorMessages = Object.values(fieldRules)
					.map((rule) => rule.errorMessage)
					.toString();

				return new window.ApplePayError(errorSection, fieldName, errorMessages);
			});
		}

		return errors;
	}

	/**
	 * Returns Apple Pay errors from the BE response of the handled event
	 * @private
	 * @param {TApplePayResponseValidationEvent} event - response validation event
	 * @returns {Array<TApplePayError>} - an array of Apple Pay errors
	 */
	private getApplePayErrorsFromResponseEvent(event: TApplePayResponseValidationEvent): Array<TApplePayError> {
		const eventData = event.detail;
		const errors = eventData.errorFields.map(
			(errorField, index) =>
				new window.ApplePayError(eventData.errorSection, errorField, eventData.errorMessages[index]),
		);

		return errors;
	}
}

export default ApplePayDefaultHandler;
